export const POST_LOGIN_REDIRECT_KEY = "postLoginRedirect"

/**
 * Routes that are part of the sign-in flow. Redirecting to one of these after a
 * successful login would bounce the user straight back to a login page.
 *
 * This is the canonical list - import it rather than copying it.
 */
export const SESSION_ROUTES = [
  "/sign-in",
  "/sign-up",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/deals-sign-in",
]

/**
 * A redirect target is only safe if it stays inside the app and isn't a session
 * route (which would bounce the user straight back to login).
 *
 * Browsers treat a leading "//" or "/\\" as protocol-relative, so "//evil.example"
 * would leave the site entirely - reject those as well as absolute URLs.
 */
function isSafeRedirectPath(path: string | null | undefined): path is string {
  if (!path || !path.startsWith("/") || path === "/" || /^\/[/\\]/.test(path)) {
    return false
  }
  // Callers build paths as pathname + search + hash, so strip both before
  // comparing: "/sign-in#foo" is still the sign-in page. Trailing slashes go too,
  // because React Router matches "/sign-in/" against an exact "/sign-in" route.
  const pathname = path.split(/[?#]/)[0].replace(/\/+$/, "")
  return !SESSION_ROUTES.includes(pathname)
}

export function savePostLoginRedirect(path: string) {
  if (!isSafeRedirectPath(path)) {
    return
  }
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path)
}

export function consumePostLoginRedirect(): string | null {
  const path = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
  // Clear on any stored value, not just a truthy one - an empty string would
  // otherwise sit in storage forever.
  if (path !== null) {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  }
  return path
}

export function redirectAfterLogin(history: { replace: (path: string) => void }) {
  // Re-check on the way out as well as on the way in: the stored value could have
  // been written by an older build, or edited by hand.
  const saved = consumePostLoginRedirect()
  history.replace(isSafeRedirectPath(saved) ? saved : "/")
}
